import { LegacyTopicArticleUtil, Topic, TopicArticle } from 'shared/models/legacy';
import { Link } from '@remix-run/react';
import { useCallback, FC, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '~/components/image';
import { useNumFormatter } from '~/utils/format/num-formatter';
import VideoIcon from 'icon/play_fill.svg';
import HeartIcon from 'icon/heart.svg';
import { useAtomValue } from 'jotai';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentPageModule } from '~/models/current-page-module';
import { useSEOHref } from '~/utils/build-seo-url';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { EArticleClass } from 'shared/models';

import './style.scss';

interface TopicArticleCardProps {
  topic: Topic;
  article: TopicArticle;
  positionPrefix?: ClickPositionPrefix;
}

export const TopicArticleCard: FC<TopicArticleCardProps> = ({
  topic,
  article,
  positionPrefix = ClickPositionPrefix.none,
}) => {
  const { topicId } = topic;
  const { articleClass, articleLikes, shortContent, title, largeImage, groupId } = article;
  const { t } = useTranslation();
  const formatNum = useNumFormatter();
  const active = usePageActive();

  const isMobileUser = useAtomValue($IsMobileUser);
  const { isBot } = useAtomValue($UserAgent);
  const currentPageModule = useScopedAtomValue($CurrentPageModule);

  const articleHref = useSEOHref(LegacyTopicArticleUtil.getArticlePath(article));

  const handleArticleClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: positionPrefix + ClickPosition.related_hashtag_article_card,
        page_click_id: groupId,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: articleClass === EArticleClass.VIDEO ? 'video' : 'article',
            groupId,
          },
          ampExtra: {
            enter_position: positionPrefix + ClickPosition.related_hashtag_article_card,
          },
        });
      },
      defaultBehavior: (event?: SyntheticEvent) => {
        event?.stopPropagation();
      },
    },
    [active, topicId, groupId],
  );

  const hashtagArticleCardRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: currentPageModule,
          current_position: positionPrefix + ClickPosition.related_hashtag_article_card,
        });
      }
    }, [isMobileUser]),
  });

  return (
    <object>
      <Link
        className="hashtag-article-card"
        to={articleHref}
        onClick={handleArticleClick}
        draggable={false}
        ref={hashtagArticleCardRef}
      >
        <div className="hashtag-article-card-cover">
          <div className="hashtag-article-card-cover-image">
            <Image src={largeImage.url} alt={t('own_picture').replace('%s', article.title || shortContent)} />
          </div>
          <div className="hashtag-article-card-cover-bg" />
          <div className="hashtag-article-card-cover-likes-count">
            <HeartIcon />
            <span>{formatNum(articleLikes)}</span>
          </div>
          {articleClass === EArticleClass.VIDEO && (
            <div className="hashtag-article-card-cover-video-badge">
              <VideoIcon />
            </div>
          )}
        </div>
        <div className="hashtag-article-card-detail">
          {isBot ? (
            <h3 className="hashtag-article-card-detail-title">{title}</h3>
          ) : (
            <div className="hashtag-article-card-detail-title">{title}</div>
          )}
          <p className="hashtag-article-card-detail-desc">{shortContent}</p>
        </div>
      </Link>
    </object>
  );
};
